import { useEffect } from 'react';
import {
  buildHotelPriceJsonLd,
  injectHotelPriceJsonLd,
  removeHotelPriceJsonLd,
  roomsPriceSignature,
} from '../utils/hotelPriceStructuredData';

/**
 * Injects Google Hotel-Price JSON-LD into <head> whenever the resolved hotel +
 * rooms data is available, and cleans it up on unmount / when data clears.
 *
 * Crash-safe by design: every input may be undefined/null/empty — the builder
 * guards each field and returns null (→ nothing injected) rather than throwing,
 * and the effect body + cleanup are wrapped in try/catch. Structured data must
 * NEVER break rendering or the app.
 */
export function useHotelPriceJsonLd(input) {
  const src = input && typeof input === 'object' ? input : {};
  const { name, identifier, address, country, checkinDate, checkoutDate, rooms } = src;
  // Stable dep signature (the rooms array ref changes every render). Safe on null/undefined.
  const roomsSig = roomsPriceSignature(rooms);
  useEffect(() => {
    try {
      const data = buildHotelPriceJsonLd({
        name,
        identifier,
        address,
        country,
        checkinDate,
        checkoutDate,
        rooms,
      });
      injectHotelPriceJsonLd(data); // data === null → removes any stale tag, no crash
    } catch (e) {
      /* structured data must never break rendering */
    }
    return () => {
      try {
        removeHotelPriceJsonLd();
      } catch (e) {
        /* no-op */
      }
    };
    // roomsSig captures rooms; other primitives listed explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, identifier, address, country, checkinDate, checkoutDate, roomsSig]);
}
